<script setup>
/**
 * A day's threads (journal practice 2026-10-28, DESIGN §2, §7b): small ❧ tags
 * under the date, the main one in gold; a tag opens its thread. The ✎ beside
 * them turns each tag into its name in a field (a rename renames the thread
 * everywhere), each with × (untags this day only), and + Add a thread: one of
 * the seeker's other threads ("Or add: …") or a new name. Cancel · Done.
 */
import { ref, computed, nextTick } from 'vue'

const props = defineProps({
  threads: { type: Array, default: () => [] },   // [{ id, name, main }]
  others: { type: Array, default: () => [] },    // the seeker's other threads
})
const emit = defineEmits(['open', 'save'])

const editing = ref(false)
const rows = ref([])        // [{ id, name, was, main }]  id '' = new, by name
const adding = ref(false)
const fresh = ref('')
const newRef = ref(null)

const suggestions = computed(() => props.others.filter((t) => !rows.value.some((r) => r.id === t.id)).slice(0, 4))

function start () {
  rows.value = props.threads.map((t) => ({ id: t.id, name: t.name, was: t.name, main: t.main }))
  adding.value = false; fresh.value = ''
  editing.value = true
}
function remove (i) { rows.value = rows.value.filter((_, j) => j !== i) }
function addOther (t) { rows.value = [...rows.value, { id: t.id, name: t.name, was: t.name, main: false }] }
async function openNew () { adding.value = true; await nextTick(); newRef.value && newRef.value.focus() }
function cancel () { editing.value = false }
function done () {
  const list = rows.value.map((r) => ({ ...r, name: r.name.replace(/\s+/g, ' ').trim() || r.was }))
  const name = fresh.value.replace(/\s+/g, ' ').trim()
  if (name) list.push({ id: '', name, was: '', main: false })
  const before = new Set(props.threads.map((t) => t.id))
  const known = (r) => r.id !== '' && r.id !== null && r.id !== undefined
  const after = new Set(list.filter(known).map((r) => r.id))
  const renames = list.filter((r) => known(r) && before.has(r.id) && r.name !== r.was).map((r) => ({ id: r.id, name: r.name }))
  const removed = props.threads.filter((t) => !after.has(t.id)).map((t) => t.id)
  const added = list.filter((r) => !known(r) || !before.has(r.id)).map((r) => ({ id: r.id, name: r.name }))
  editing.value = false
  if (!renames.length && !removed.length && !added.length) return
  const threads = list.map((r, i) => ({ id: r.id, name: r.name, main: i === 0 }))
  emit('save', { renames, removed, added, threads })
}
function onKey (e) {
  if (e.key === 'Escape') { e.preventDefault(); cancel() }
  if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); done() }
}
</script>

<template>
  <div class="tt" data-testid="journal-thread-tags" @pointerdown.stop @pointerup.stop>
    <template v-if="!editing">
      <div class="th-tags">
        <button v-for="t in threads" :key="t.id" type="button" class="tag" :class="{ main: t.main, dim: !t.main }" :aria-label="`Open the thread ${t.name}`" data-testid="journal-thread-tag" @click.stop="emit('open', t.id)">{{ t.name }}</button>
        <button type="button" class="pen" aria-label="Change this day’s threads" data-testid="journal-tags-edit" @click.stop="start">✎</button>
      </div>
    </template>
    <template v-else>
      <div class="th-tags" @keydown="onKey">
        <span v-for="(r, i) in rows" :key="r.id || 'n' + i" class="tag" :class="{ dim: i > 0 }">
          <input v-model="r.name" :size="Math.max(6, r.name.length)" :aria-label="`Rename ${r.was}`" data-testid="journal-tag-name" />
          <button type="button" class="x" :aria-label="`Take ${r.was} off this day`" data-testid="journal-tag-remove" @click.stop="remove(i)">×</button>
        </span>
        <span v-if="adding" class="tag add on"><input ref="newRef" v-model="fresh" size="14" placeholder="A new thread" aria-label="A new thread’s name" data-testid="journal-tag-new" /></span>
        <button v-else type="button" class="tag add" data-testid="journal-tag-add" @click.stop="openNew">Add a thread</button>
      </div>
      <p v-if="suggestions.length" class="sugg" data-testid="journal-tags-suggest">Or add:<button v-for="t in suggestions" :key="t.id" type="button" data-testid="journal-tag-suggestion" @click.stop="addOther(t)">{{ t.name }}</button></p>
      <div class="donebar">
        <span class="sp">Renaming here renames the thread everywhere.</span>
        <button type="button" class="c" data-testid="journal-tags-cancel" @click.stop="cancel">Cancel</button>
        <button type="button" class="d" data-testid="journal-tags-done" @click.stop="done">Done</button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.tt { margin: 12px 0 0; }
.th-tags { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.tag { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px 4px 9px; min-height: 30px; box-sizing: border-box; border-radius: 999px; border: 1px solid rgba(140, 102, 40, .45); font: 600 13px var(--j-ui); color: var(--j-hand); background: rgba(255, 250, 236, .6); cursor: pointer; }
.tag::before { content: '❧'; font-size: 13px; }
.tag.dim { color: var(--j-ink3); border-color: rgba(42, 24, 69, .18); }
.tag:hover { background: rgba(255, 250, 236, .95); }
.pen { border: 0; background: none; padding: 6px; cursor: pointer; font: 600 12px var(--j-ui); color: var(--j-hand); opacity: .55; }
.pen:hover { opacity: .9; }
.tag input { min-width: 0; border: 0; border-bottom: 1.5px dashed var(--j-hand); background: none; font: 600 13px var(--j-ui); color: var(--j-hand); outline: none; padding: 0; }
.tag.dim input { color: var(--j-ink3); }
.tag .x { margin-left: 2px; border: 0; background: none; padding: 0 2px; font-size: 14px; line-height: 1; color: var(--j-ink3); cursor: pointer; }
.tag.add { border-style: dashed; color: var(--j-ink3); border-color: rgba(140, 102, 40, .45); }
.tag.add::before { content: '+'; }
.tag.add.on input { color: var(--j-ink); }
.sugg { margin: 6px 0 0; font: 500 12.5px var(--j-ui); color: var(--j-ink3); }
.sugg button { border: 0; background: none; padding: 2px 0; margin-left: 6px; cursor: pointer; color: var(--j-hand); font: 600 12.5px var(--j-ui); text-decoration: underline; text-underline-offset: 3px; }
.donebar { display: flex; justify-content: flex-end; align-items: center; gap: 10px; margin: 6px 0 4px; font: 500 12px/1.3 var(--j-ui); color: var(--j-ink3); }
.donebar .sp { margin-right: auto; }
.donebar button { border: 0; cursor: pointer; font: 600 13px var(--j-ui); }
.donebar .c { color: var(--j-ink2); padding: 6px 4px; background: none; }
.donebar .d { color: #ffdfa3; background: linear-gradient(180deg, #7b4bb0, #4e2776); padding: 7px 16px; border-radius: 999px; }
</style>
